// Agent-Inventar + Live-Status für Dashboard-Tile und Settings-Page.
//
// Datenfluss (seit 02.11.2026 über das Homelab-Protokoll, docs/protokoll.md
// › „Agenten über das Protokoll“): jeder Agent ist eine Instanz
// agent.<id>.stand, jeder Kandidat eine Instanz agent.kandidat.<id>. Das
// Backend (internal/protokoll/agenten) gleicht Inventar und Live-Status alle
// 2 s und bei jedem Anstoß (Heartbeat, Last Will, Deploy-Wechsel,
// Statuswechsel) ab; der Hub verwirft Unverändertes und schickt Deltas. Bis
// dahin holte der Store alle 5-30 s beide Listen per REST -- je Tab.
//   - REST (`/v2-api/agents`, `/agents/candidates`) beim Start, als
//     Revalidator (Reconnect, Tab kommt zurück), in refreshAgents (nach einer
//     Aktion der Seite: speichern, löschen, übernehmen, verwerfen, ausrollen)
//     und als Neuversuch alle 15 s, solange ein Abruf scheiterte. KEIN
//     Takt, KEIN Abo auf den alten WS-Kanal "agents" mehr.
//   - `agent.*.stand` und `agent.kandidat.*` (Rate normal): Snapshot + Delta je
//     Instanz. Ein Stand aktualisiert den Eintrag in der Liste in-place
//     (Objekt-Identität bleibt), ein unbekannter kommt an seinen Platz der
//     REST-Reihenfolge, `weg` nimmt ihn heraus. Die Id des Agenten steht im
//     Instanznamen; den Kandidaten findet der Store über das Topic aus dem
//     Stand (`weg` kennt nur den Instanznamen, darum merkt er sich
//     Instanz -> Topic). Der jüngste Stand je Eintrag wird mit laufender
//     Nummer gemerkt; kam er während eines REST-Abrufs, liegt er über dessen
//     Antwort (sie kann dahinter liegen), ältere nicht. Ein `weg`
//     hinterlässt einen Grabstein, damit eine Antwort, die den Eintrag noch
//     nennt, ihn nicht zurückholt.
//   - last_seen_age_s steht nicht im Stand (es wäre jede 2 s ein Delta): der
//     Store rechnet es aus last_seen und der Uhr des Browsers und tickt es alle
//     5 s nach, solange er läuft.
//
// Das Backend bleibt Source of Truth für Stale-Schwellen und Status; dass ein
// ruhender Agent nach der Stille-Schwelle "stale" wird, meldet es selbst als
// Delta (der Abgleich läuft auch ohne Heartbeat).
//
// Wire-Form mirrort internal/agents/store.go (View, Candidate); aus dem
// Protokoll-Stand baut sie lib/agenten-protokoll.ts.
//
// SWR: ja -- Dashboard-Tile + /infra/agents zeigen beim Reload sofort die
// letzten Werte.

import { getJSON } from "./api";
import {
  agentAusProtokoll,
  agentIdAusInstanz,
  kandidatAusProtokoll,
  kandidatKennungAusInstanz,
  setzeAlter,
  vergleicheAgenten,
  vergleicheKandidaten,
} from "./agenten-protokoll";
import { loadPersisted, persist } from "./persistent-summary.svelte";
import { abonniere } from "./protokoll/client.svelte";
import type { AgentKandidat, AgentStand } from "./protokoll/schema.gen";
import { skaliere } from "./protokoll/skaliere";
import { registerRevalidator } from "./revalidate.svelte";
import { mitStaenden, uebernimm, type Gemerkt } from "./storage-protokoll";

// "cluster_disabled" = Agent ist aktiv, sein Cluster aber bewusst stillgelegt
// (entity_disables, Domain "cluster"). Fehlender Heartbeat ist dann erwartet
// -- UI zeigt das erklaerend (nicht als stale/rot). Backend setzt das in
// store.attachStatus, analog zum Watchdog-Skip des Auto-Deploys.
export type AgentStatus = "ok" | "stale" | "never_seen" | "disabled" | "cluster_disabled";
// "incompatible" = permanenter Deploy-Fehler (z.B. host-stats auf macOS
// ohne systemd). Watchdog ueberspringt; UI zeigt erklaerend, nicht alarm-
// ierend. Backend setzt das in deployer.go bei exit-code 2 des Roll-out-
// Skripts.
// "unreachable" = der Deploy-Pfad (SSH/Tailscale) ist gerade nicht
// erreichbar (z.B. SON-Vodafone-Re-Sync). KEIN Fehler -- der Watchdog
// wartet still und eskaliert erst nach laengerer Unerreichbarkeit. UI
// zeigt das dezent ("pausiert"), nicht alarmierend. Orthogonal zum
// Stats-Status: ein Host kann ok pushen und trotzdem unreachable sein.
export type DeployStatus = "idle" | "deploying" | "ok" | "error" | "incompatible" | "unreachable";

export interface DeployState {
  status: DeployStatus;
  last_try?: string;
  last_ok?: string;
  last_err?: string;
  attempts: number;
  unreachable_since?: string;
}

export interface AgentView {
  id: number;
  name: string;
  kind: string;
  mqtt_topic: string;
  expected_interval_s: number;
  data_interval_s: number;
  max_silence_s: number;
  ssh_alias: string;
  enabled: boolean;
  notes: string;
  status: AgentStatus;
  last_seen?: string;
  last_seen_age_s?: number;
  // Gesetzt, solange der Agent stale ist, WEIL er sich per Last Will
  // abgemeldet hat (statt bloss zu verstummen).
  offline_since?: string;
  deploy?: DeployState;
  // Gemessenes tatsaechliches Sende-Intervall (Mittelwert ueber die
  // letzten 2..10 Frames). Wenn vorhanden, kann das Frontend daneben
  // den Soll-Wert anzeigen + bei groesserer Abweichung warnen.
  measured_interval_s?: number;
  interval_samples?: number;
  interval_skew_pct?: number;
  // Soll-Wert, gegen den interval_skew_pct gerechnet wurde. Meist =
  // data_interval_s, waehrend eines aktiven Boost-on-View aber das
  // befohlene kuerzere Intervall. Das UI zeigt genau diesen Nenner.
  expected_interval_effective_s?: number;
  created_at: string;
  updated_at: string;
}

// Candidate = beobachtetes Topic, das (noch) nicht im agents-Inventar
// steht. Discovery legt diese Kandidaten an, der User adoptiert oder
// verwirft sie explizit. Damit kann ein retained-Ghost-Frame oder ein
// einmaliges Test-Publish keinen produktiven Eintrag mehr erzeugen.
export interface AgentCandidate {
  topic: string;
  kind: string;
  default_name: string;
  frame_count: number;
  first_seen: string;
  last_seen: string;
}

const STORAGE_KEY_AGENTS = "agents";
const STORAGE_KEY_CANDIDATES = "agents-candidates";

// Tief reaktiv: Seiten lesen Felder, ein Stand ändert nur die betroffenen.
let _agents = $state<AgentView[]>([]);
let _candidates = $state<AgentCandidate[]>([]);
let _started = false;
let _laedt = true;
let _unsub: Array<() => void> = [];

// Der jüngste Protokoll-Stand je Eintrag, mit laufender Nummer des
// Eintreffens: ein Abruf legt nur Stände über seine Antwort, die nach seinem
// Start kamen -- ältere hat der Server beim Bauen der Antwort schon gekannt
// (und nach einer Unterbrechung des Protokolls wären sie veraltet). `e: null`
// = der Eintrag ist weg: eine Antwort, die ihn noch nennt, holt ihn nicht
// zurück. Schlüssel: Agent-Id als Text, Kandidat: Topic.
const _merkAgents = new Map<string, Gemerkt<AgentView>>();
const _merkCands = new Map<string, Gemerkt<AgentCandidate>>();
let _nr = 0;
// Instanzname eines Kandidaten -> Topic: `weg` nennt nur die Instanz.
const _topics = new Map<string, string>();

// ── Alter von last_seen ──────────────────────────────────────────────

// Das Alter rechnet der Browser (siehe Kopf); ein Tick genügt, weil die
// Anzeige in Sekunden-/Minutenschritten läuft ("vor 7 s").
const ALTER_TICK_MS = 5_000;
let _alterTimer: ReturnType<typeof setInterval> | undefined;

function alterNachtragen(): void {
  const jetzt = Date.now();
  for (const a of _agents) setzeAlter(a, jetzt);
}

// ── Persistenz ───────────────────────────────────────────────────────

// Aus dem Protokoll kommt oft etwas (last_seen und Takt-Messung ändern sich
// mit jedem Heartbeat): gespeichert wird gebündelt, höchstens alle 2 s.
let _persistTimer: ReturnType<typeof setTimeout> | undefined;
function persistSpaeter(): void {
  if (_persistTimer) return;
  _persistTimer = setTimeout(() => {
    _persistTimer = undefined;
    persist(STORAGE_KEY_AGENTS, $state.snapshot(_agents));
    persist(STORAGE_KEY_CANDIDATES, $state.snapshot(_candidates));
  }, 2_000);
}

// ── Reihenfolge ──────────────────────────────────────────────────────

// Die Liste in der Reihenfolge der REST-Antwort; nur bei Abweichung neu
// gesetzt (die Einträge behalten ihre Identität).
function ordneAgents(): void {
  const next = [..._agents].sort(vergleicheAgenten);
  if (next.some((e, i) => e !== _agents[i])) _agents = next;
}

function ordneCands(): void {
  const next = [..._candidates].sort(vergleicheKandidaten);
  if (next.some((e, i) => e !== _candidates[i])) _candidates = next;
}

// ── Stände ──────────────────────────────────────────────────────────

function beiAgentStand(instanz: string, daten: Record<string, unknown>): void {
  const id = agentIdAusInstanz(instanz);
  if (id === undefined) return;
  const neu = agentAusProtokoll(id, skaliere<AgentStand>("agent.stand", daten));
  setzeAlter(neu, Date.now());
  _merkAgents.set(String(id), { e: neu, nr: ++_nr });
  const ex = _agents.find((x) => x.id === id);
  if (ex) {
    if (uebernimm(ex, neu)) {
      ordneAgents();
      persistSpaeter();
    }
  } else if (!_laedt) {
    // Ein Agent, den die Liste noch nicht kennt (eben angelegt). Vor dem
    // ersten Abruf legt laden() den Stand über die Antwort.
    _agents.push(neu);
    ordneAgents();
    persistSpaeter();
  }
}

function beiAgentWeg(instanz: string): void {
  const id = agentIdAusInstanz(instanz);
  if (id === undefined) return;
  _merkAgents.set(String(id), { e: null, nr: ++_nr });
  const i = _agents.findIndex((x) => x.id === id);
  if (i >= 0) {
    _agents.splice(i, 1);
    persistSpaeter();
  }
}

function beiKandidatStand(instanz: string, daten: Record<string, unknown>): void {
  if (kandidatKennungAusInstanz(instanz) === undefined) return;
  const neu = kandidatAusProtokoll(skaliere<AgentKandidat>("agent.kandidat", daten));
  _topics.set(instanz, neu.topic);
  _merkCands.set(neu.topic, { e: neu, nr: ++_nr });
  const ex = _candidates.find((x) => x.topic === neu.topic);
  if (ex) {
    if (uebernimm(ex, neu)) {
      ordneCands();
      persistSpaeter();
    }
  } else if (!_laedt) {
    _candidates.push(neu);
    ordneCands();
    persistSpaeter();
  }
}

function beiKandidatWeg(instanz: string): void {
  const topic = _topics.get(instanz);
  if (topic === undefined) return;
  _topics.delete(instanz);
  _merkCands.set(topic, { e: null, nr: ++_nr });
  const i = _candidates.findIndex((x) => x.topic === topic);
  if (i >= 0) {
    _candidates.splice(i, 1);
    persistSpaeter();
  }
}

// ── REST ─────────────────────────────────────────────────────────────

// Gab es seit `ab` etwas aus dem Protokoll?
function neuereStaende(ab: number): boolean {
  return [..._merkAgents.values(), ..._merkCands.values()].some((g) => g.nr > ab);
}

// Die frische Antwort (oder, wenn der Abruf scheiterte, die vorhandene Liste)
// plus die Protokoll-Stände, die während des Abrufs kamen, IN-PLACE in die
// Liste: vorhandene Einträge behalten Objekt-Identität (nur Felder werden
// aktualisiert), Reihenfolge der REST-Antwort, Neue an ihrem Platz.
function mische<T extends object>(
  cur: T[],
  rest: T[] | undefined,
  merk: Map<string, Gemerkt<T>>,
  ab: number,
  schluessel: (e: T) => string,
  vergleiche: (a: T, b: T) => number,
): T[] | undefined {
  if (!rest && !neuereStaende(ab)) return undefined;
  const alle = mitStaenden(rest ?? cur, merk, ab, schluessel);
  const vorhanden = new Map(cur.map((e) => [schluessel(e), e]));
  const next = alle.map((e) => {
    const ex = vorhanden.get(schluessel(e));
    if (ex) {
      uebernimm(ex, e);
      return ex;
    }
    return e;
  });
  next.sort(vergleiche);
  const same = next.length === cur.length && next.every((e, i) => e === cur[i]);
  return same ? undefined : next;
}

function anwenden(agents: AgentView[] | undefined, cands: AgentCandidate[] | undefined, ab: number): void {
  if (agents) for (const a of agents) setzeAlter(a, Date.now());
  const a = mische(_agents, agents, _merkAgents, ab, (e) => String(e.id), vergleicheAgenten);
  if (a) _agents = a;
  const c = mische(_candidates, cands, _merkCands, ab, (e) => e.topic, vergleicheKandidaten);
  if (c) _candidates = c;
  persistSpaeter();
}

// Nach einem gescheiterten Abruf ein neuer Versuch: ohne Takt käme sonst
// erst der nächste Revalidator -- und bis dahin bliebe ein aus der Ablage
// geholter, inzwischen gelöschter Eintrag stehen (für ihn kommt kein `weg`,
// diese Sitzung kannte seine Instanz nie).
const NEUVERSUCH_MS = 15_000;
let _neuversuch: ReturnType<typeof setTimeout> | undefined;

// Lauf: zählt Start und Stopp hoch. Eine Antwort, die erst nach dem Stopp
// (oder nach einem neuen Start) ankommt, gehört zu keinem laufenden Store.
let _lauf = 0;

async function laden(): Promise<void> {
  const ab = _nr;
  const lauf = _lauf;
  // Die beiden Listen getrennt: scheitert eine, gilt die andere trotzdem.
  const [a, c] = await Promise.allSettled([
    getJSON<AgentView[]>("/v2-api/agents"),
    getJSON<AgentCandidate[]>("/v2-api/agents/candidates"),
  ]);
  if (lauf !== _lauf) return;
  try {
    // Was das Protokoll während eines gescheiterten Abrufs gebracht hat, gilt
    // trotzdem -- beim ersten Abruf wären die Listen sonst leer, bis jeder
    // Eintrag sich ändert.
    anwenden(a.status === "fulfilled" ? a.value : undefined, c.status === "fulfilled" ? c.value : undefined, ab);
  } finally {
    _laedt = false;
  }
  if ((a.status === "rejected" || c.status === "rejected") && _started && !_neuversuch) {
    _neuversuch = setTimeout(() => {
      _neuversuch = undefined;
      if (_started) void laden();
    }, NEUVERSUCH_MS);
  }
}

export function startAgentsSummary(): void {
  if (_started) return;
  _started = true;
  _lauf++;
  _laedt = true;
  // SWR: gecachte Liste sofort einspielen. Das Protokoll bringt den Live-
  // Status danach in Sekunden frisch, der Cache deckt nur die First-Paint-
  // Lücke.
  const cachedAgents = loadPersisted<AgentView[]>(STORAGE_KEY_AGENTS);
  if (cachedAgents) {
    _agents = cachedAgents;
    alterNachtragen();
  }
  const cachedCands = loadPersisted<AgentCandidate[]>(STORAGE_KEY_CANDIDATES);
  if (cachedCands) _candidates = cachedCands;
  void laden();
  _unsub.push(
    abonniere(
      "agent.*.stand",
      { rate: "normal" },
      { stand: beiAgentStand, weg: beiAgentWeg },
    ),
    abonniere(
      "agent.kandidat.*",
      { rate: "normal" },
      { stand: beiKandidatStand, weg: beiKandidatWeg },
    ),
  );
  _alterTimer = setInterval(alterNachtragen, ALTER_TICK_MS);
  // ⚠️ Der Protokoll-Kanal allein reicht NICHT. Reißt die Leitung ab -- und
  // jeder Deploy tut das --, sind verpasste Löschungen weg. registerRevalidator
  // holt beim Reconnect und beim Zurückkommen des Tabs alles frisch. (Der
  // Protokoll-Client holt sich nach einer Lücke selbst einen Snapshot je
  // Instanz; die Revalidation deckt zusätzlich Einträge ab, die während der
  // Lücke verschwanden.)
  _unsub.push(registerRevalidator(laden));
}

export function stopAgentsSummary(): void {
  _started = false;
  _lauf++;
  for (const off of _unsub) off();
  _unsub = [];
  if (_alterTimer) {
    clearInterval(_alterTimer);
    _alterTimer = undefined;
  }
  if (_persistTimer) {
    clearTimeout(_persistTimer);
    _persistTimer = undefined;
  }
  if (_neuversuch) {
    clearTimeout(_neuversuch);
    _neuversuch = undefined;
  }
  _agents = [];
  _candidates = [];
  _merkAgents.clear();
  _merkCands.clear();
  _topics.clear();
}

export function getAgents(): AgentView[] {
  return _agents;
}

export function getAgentCandidates(): AgentCandidate[] {
  return _candidates;
}

/** Holt beide Listen sofort per REST -- nach einer Aktion, deren Ergebnis die
 *  Seite gleich zeigen soll (das Protokoll braucht bis zu 2 s). */
export function refreshAgents(): Promise<void> {
  // Nach einem Stopp (Seite verlassen, Aktion endet später) nichts mehr füllen.
  if (!_started) return Promise.resolve();
  return laden();
}

// formatLastSeen liefert "vor 7 s" / "vor 12 min" / "—" — genuegt fuer
// Status-Karten. Vermeidet Date-Lib, lebt mit Sekunden-Granularitaet.
export function formatLastSeen(ageS: number | undefined): string {
  if (ageS == null) return "—";
  // Sub-Minuten als "vor X s" inkl. 0 -- siehe formatRelative in format.ts.
  if (ageS < 60) return `vor ${Math.max(0, Math.round(ageS))} s`;
  if (ageS < 3600) return `vor ${Math.round(ageS / 60)} min`;
  if (ageS < 86400) return `vor ${Math.round(ageS / 3600)} h`;
  return `vor ${Math.round(ageS / 86400)} d`;
}
